import { inquiryClient } from "@/services";
import { useMutation } from "@tanstack/react-query";
import {
  IBankValidationMutationPayload,
  ICreditScoreSendOtpMutationPayload,
  ICreditScoreVerifyOtpMutationPayload,
} from "@/interfaces/inquiries/creditScore";
import {
  IChequeInquiryMutationPayload,
  IChequeTransfersChainInquiryMutationPayload,
  IRegisterChequeMutationPayload,
} from "@/interfaces/inquiries/chequeInquiry";
import {
  IBackChequesAuthorizePayload,
  IBackChequesPayload,
  IBackChequesVerifyPayload,
  IActiveLoansInquiryMutationPayload,
  IBouncedCheckInquiryMutationPayload,
  ICardsInquiryMutationPayload,
  ICardToDepositInquiryMutationPayload,
  ICardToIbanInquiryMutationPayload,
  IDepositToIbanInquiryMutationPayload,
  IIbansInquiryMutationPayload,
} from "@/interfaces/inquiries/bank";
import {
  ICompanyInfoInquiryMutationPayload,
  ICompanyNewspaperMutationPayload,
  ICorporateIdentityInquiryMutationPayload,
  ITaxRecordsInquiryMutationPayload,
} from "@/interfaces/inquiries/legal";
import {
  IBillPayload,
  IElectricityBillImagePayload,
  IForeignersInquiryMutationPayload,
  IGasBillInfoPayload,
  IGasBillPayload,
  IIdentityInquiryMutationPayload,
  IMilitaryServiceInquiryMutationPayload,
  IMobileBillPayload,
  IPassportStatusInquiryMutationPayload,
  IPostalCodeInquiryMutationPayload,
  IPostalTrackingMutationPayload,
  ISanaInquiryMutationPayload,
} from "@/interfaces/inquiries/governmental";
import {
  ICardDocumentMutationPayload,
  IFreewayTollInquiryPayload,
  IMotorcycleFinesMutationPayload,
  IDrivingLicenseStatusInquiryMutationPayload,
  INegativePointsInquiryMutationPayload,
  IPlateInquiryMutationPayload,
  IThirdPartyInsuranceInquiryPayload,
  ITrafficFinesMutationPayload,
  ITrafficFinesPhotoMutationPayload,
  IVehicleInfoInquiryPayload,
  IVehicleTransferTaxInquiryPayload,
  IVehicleTransferTaxPayPayload,
} from "@/interfaces/inquiries/vehicle";

export const useIbansInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IIbansInquiryMutationPayload }) =>
      inquiryClient.ibansInquiryMutation({ token, payload }),
  });

export const useCardsInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: ICardsInquiryMutationPayload }) =>
      inquiryClient.cardsInquiryMutation({ token, payload }),
  });

export const useCardToIbanInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: ICardToIbanInquiryMutationPayload }) =>
      inquiryClient.cardToIbanInquiryMutation({ token, payload }),
  });

export const useCardToDepositInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: ICardToDepositInquiryMutationPayload;
    }) => inquiryClient.cardToDepositInquiryMutation({ token, payload }),
  });

export const useDepositToIbanInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: IDepositToIbanInquiryMutationPayload;
    }) => inquiryClient.depositToIbanInquiryMutation({ token, payload }),
  });

export const useBouncedCheckInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: IBouncedCheckInquiryMutationPayload;
    }) => inquiryClient.bouncedCheckInquiryMutation({ token, payload }),
  });

export const useActiveLoansInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: IActiveLoansInquiryMutationPayload;
    }) => inquiryClient.activeLoansInquiryMutation({ token, payload }),
  });

export const useBouncedCheckDetailsInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: IBouncedCheckInquiryMutationPayload;
    }) => inquiryClient.bouncedCheckDetailsInquiryMutation({ token, payload }),
  });

export const useBackChequesAuthorizeMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IBackChequesAuthorizePayload }) =>
      inquiryClient.backChequesAuthorizeMutation({ token, payload }),
  });

export const useBackChequesVerifyMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IBackChequesVerifyPayload }) =>
      inquiryClient.backChequesVerifyMutation({ token, payload }),
  });

export const useBackChequesMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IBackChequesPayload }) =>
      inquiryClient.backChequesMutation({ token, payload }),
  });

export const useCompanyInfoInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: ICompanyInfoInquiryMutationPayload;
    }) => inquiryClient.companyInfoInquiryMutation({ token, payload }),
  });

export const useTaxRecordsInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: ITaxRecordsInquiryMutationPayload;
    }) => inquiryClient.taxRecordsInquiryMutation({ token, payload }),
  });

export const useCompanyMembersInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: ICompanyInfoInquiryMutationPayload;
    }) => inquiryClient.companyMemberInquiryMutation({ token, payload }),
  });

export const useCompanyLicensesInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: ICompanyInfoInquiryMutationPayload;
    }) => inquiryClient.companyLicensesInquiryMutation({ token, payload }),
  });

export const useCompanyParentsInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: ICompanyInfoInquiryMutationPayload;
    }) => inquiryClient.companyParentsInquiryMutation({ token, payload }),
  });

export const useCompanyChildrenInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: ICompanyInfoInquiryMutationPayload;
    }) => inquiryClient.companyChildrenInquiryMutation({ token, payload }),
  });

export const useMilitaryServiceInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: IMilitaryServiceInquiryMutationPayload;
    }) => inquiryClient.militaryServiceInquiryMutation({ token, payload }),
  });

export const useMilitaryCardInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: IMilitaryServiceInquiryMutationPayload;
    }) => inquiryClient.militaryCardInquiryMutation({ token, payload }),
  });

export const useEducationalExemptionInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: IMilitaryServiceInquiryMutationPayload;
    }) => inquiryClient.educationalExemptionInquiryMutation({ token, payload }),
  });

export const usePostalCodeInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IPostalCodeInquiryMutationPayload }) =>
      inquiryClient.postalCodeInquiryMutation({ token, payload }),
  });

export const useTrafficFinesMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: ITrafficFinesMutationPayload }) =>
      inquiryClient.trafficFinesMutation({ token, payload }),
  });

export const useVehicleTransferTaxInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IVehicleTransferTaxInquiryPayload }) =>
      inquiryClient.vehicleTransferTaxInquiryMutation({ token, payload }),
  });

export const useFreewayTollInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IFreewayTollInquiryPayload }) =>
      inquiryClient.freewayTollInquiryMutation({ token, payload }),
  });

export const useVehicleInfoInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IVehicleInfoInquiryPayload }) =>
      inquiryClient.vehicleInfoInquiryMutation({ token, payload }),
  });

export const useThirdPartyInsuranceInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IThirdPartyInsuranceInquiryPayload }) =>
      inquiryClient.thirdPartyInsuranceInquiryMutation({ token, payload }),
  });

export const useVehicleTransferTaxPayMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IVehicleTransferTaxPayPayload }) =>
      inquiryClient.vehicleTransferTaxPayMutation({ token, payload }),
  });

export const useVehicleTransferTaxDropMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IVehicleTransferTaxPayPayload }) =>
      inquiryClient.vehicleTransferTaxDropMutation({ token, payload }),
  });

export const useTrafficFinesPhotoMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: ITrafficFinesPhotoMutationPayload }) =>
      inquiryClient.trafficFinesPhotoMutation({ token, payload }),
  });

export const useMotorcycleFinesMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IMotorcycleFinesMutationPayload }) =>
      inquiryClient.motorcycleFinesMutation({ token, payload }),
  });

export const usePlateInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IPlateInquiryMutationPayload }) =>
      inquiryClient.plateInquiryMutation({ token, payload }),
  });

export const useNegativePointsInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: INegativePointsInquiryMutationPayload;
    }) => inquiryClient.negativePointsInquiryMutation({ token, payload }),
  });

export const useDrivingLicenseStatusInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: IDrivingLicenseStatusInquiryMutationPayload;
    }) => inquiryClient.drivingLicenseStatusInquiryMutation({ token, payload }),
  });
export const usePassportStatusInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: IPassportStatusInquiryMutationPayload;
    }) => inquiryClient.passportStatusInquiryMutation({ token, payload }),
  });

export const useSanaInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: ISanaInquiryMutationPayload }) =>
      inquiryClient.sanaInquiryMutation({ token, payload }),
  });

export const useIdentityInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IIdentityInquiryMutationPayload }) =>
      inquiryClient.identityInquiryMutation({ token, payload }),
  });

export const useBankValidationMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IBankValidationMutationPayload }) =>
      inquiryClient.bankValidationMutation({ token, payload }),
  });

export const useInternalBankValidationMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IBankValidationMutationPayload }) =>
      inquiryClient.internalBankValidationMutation({ token, payload }),
  });

export const useBankRevalidationMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IBankValidationMutationPayload }) =>
      inquiryClient.revalidateBankValidationMutation({ token, payload }),
  });

export const useBankValidationSendOtpMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: ICreditScoreSendOtpMutationPayload;
    }) => inquiryClient.creditScoreSendOtpMutation({ token, payload }),
  });

export const useBankValidationVerifyOtpMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: ICreditScoreVerifyOtpMutationPayload;
    }) => inquiryClient.creditScoreVerifyOtpMutation({ token, payload }),
  });

export const useInternalBankValidationSendOtpMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: ICreditScoreSendOtpMutationPayload;
    }) => inquiryClient.internalCreditScoreSendOtpMutation({ token, payload }),
  });

export const useInternalBankValidationVerifyOtpMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: ICreditScoreVerifyOtpMutationPayload;
    }) => inquiryClient.internalCreditScoreVerifyOtpMutation({ token, payload }),
  });

export const useForeignersInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IForeignersInquiryMutationPayload }) =>
      inquiryClient.foreignersInquiryMutation({ token, payload }),
  });

export const useCorporateIdentityInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: ICorporateIdentityInquiryMutationPayload;
    }) => inquiryClient.corporateIdentityInquiryMutation({ token, payload }),
  });

export const useCarDocumentInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: ICardDocumentMutationPayload }) =>
      inquiryClient.carDocumentInquiryMutation({ token, payload }),
  });

export const useChequeInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IRegisterChequeMutationPayload }) =>
      inquiryClient.registerChequeInquiryMutation({ token, payload }),
  });

export const useChequeV2InquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IChequeInquiryMutationPayload }) =>
      inquiryClient.chequeInquiryMutation({ token, payload }),
  });

export const useChequeTransfersChainInquiryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: IChequeTransfersChainInquiryMutationPayload;
    }) => inquiryClient.chequeTransfersChainInquiryMutation({ token, payload }),
  });

export const usePostalTrackingMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IPostalTrackingMutationPayload }) =>
      inquiryClient.postalTrackingMutation({ token, payload }),
  });

export const useCompanyNewspapersMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: ICompanyNewspaperMutationPayload }) =>
      inquiryClient.companyNewspapersMutation({ token, payload }),
  });

export const useElectricityBillMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IBillPayload }) =>
      inquiryClient.electricityBillMutation({ token, payload }),
  });

export const useElectricityBillInfoMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IBillPayload }) =>
      inquiryClient.electricityBillInfoMutation({ token, payload }),
  });

export const useElectricityBillHistoryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IBillPayload }) =>
      inquiryClient.electricityBillHistoryMutation({ token, payload }),
  });

export const useElectricityBillImageMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IElectricityBillImagePayload }) =>
      inquiryClient.electricityBillImageMutation({ token, payload }),
  });

export const useGasBillMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IGasBillPayload }) =>
      inquiryClient.gasBillMutation({ token, payload }),
  });

export const useGasBillInfoMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IGasBillInfoPayload }) =>
      inquiryClient.gasBillInfoMutation({ token, payload }),
  });

export const useGasBillHistoryMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IGasBillInfoPayload }) =>
      inquiryClient.gasBillHistoryMutation({ token, payload }),
  });

export const useMobileBillMtnMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IMobileBillPayload }) =>
      inquiryClient.mobileBillMtnMutation({ token, payload }),
  });

export const useMobileBillMciMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IMobileBillPayload }) =>
      inquiryClient.mobileBillMciMutation({ token, payload }),
  });

export const useMobileBillRightelMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IMobileBillPayload }) =>
      inquiryClient.mobileBillRightelMutation({ token, payload }),
  });

export const usePhoneBillMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IMobileBillPayload }) =>
      inquiryClient.phoneBillMutation({ token, payload }),
  });

export const useWaterBillMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IBillPayload }) =>
      inquiryClient.waterBillMutation({ token, payload }),
  });

export const useWaterBillInfoMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload: IBillPayload }) =>
      inquiryClient.waterBillInfoMutation({ token, payload }),
  });
